import React, { createContext, useContext, useState, ReactNode, useRef, useEffect, useCallback } from 'react';
import { 
  getAllItems,
  getItemById,
  addSilverItem,
  addWatchItem,
  updateItem,
  deleteItem,
  findItemByBarcode
} from '../services/inventoryService';
import { InventoryItem, SilverItem, WatchItem, BaseItem, ItemStatus, Currency, ItemType } from '../types';
import { InventoryFilterValues, ActiveRangeFilter, DateRangeFilter, PriceRangeFilter, FilterOptionAll } from '../types/filters';
import { auth } from '../services/firebase';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { getCurrentExchangeRate, convertUsdToTry } from '../services/exchangeRateService';

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
}

interface InventorySummaryData {
  totalItems: number;
  totalValue: number; // totalCost removed
}

const initialFilterValues: InventoryFilterValues = {
  status: 'all',
  itemType: 'all',
  category: 'all',
  dateRange: null,
  priceRange: null,
  barcode: null,
};

interface InventoryContextType {
  items: InventoryItem[];
  loading: boolean;
  error: string | null;
  selectedItem: InventoryItem | null;
  pagination: PaginationData;
  filters: InventoryFilterValues;
  activeRangeFilter: ActiveRangeFilter;
  inventorySummary: InventorySummaryData | null;
  loadingSummary: boolean;
  fetchItems: (page?: number, pageSize?: number, cursorForPage?: QueryDocumentSnapshot, filtersOverride?: InventoryFilterValues, activeRangeFilterOverride?: ActiveRangeFilter) => Promise<void>;
  setInventoryFilters: (newFilters: Partial<InventoryFilterValues>, newActiveRangeFilter?: ActiveRangeFilter) => void;
  fetchNextPage: () => Promise<void>;
  fetchPrevPage: () => Promise<void>;
  fetchAndCalculateInventorySummary: () => Promise<void>;
  fetchItemById: (id: string) => Promise<InventoryItem | null>;
  createSilverItem: (item: Omit<SilverItem, 'id' | 'createdAt' | 'updatedAt'>) => Promise<SilverItem>;
  createWatchItem: (item: Omit<WatchItem, 'id' | 'createdAt' | 'updatedAt'>) => Promise<WatchItem>;
  modifyItem: (id: string, item: Partial<Omit<BaseItem, 'id' | 'createdAt' | 'type'>>) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  findByBarcode: (barcode: string) => Promise<InventoryItem | null>;
  selectItem: (item: InventoryItem | null) => void;
  dataLoaded: boolean;
}

const InventoryContext = createContext<InventoryContextType | undefined>(undefined);

export const useInventory = (): InventoryContextType => {
  const context = useContext(InventoryContext);
  if (context === undefined) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
};

interface InventoryProviderProps {
  children: ReactNode;
}

export const InventoryProvider: React.FC<InventoryProviderProps> = ({ children }) => {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10
  });
  const [filters, setFilters] = useState<InventoryFilterValues>(initialFilterValues);
  const [activeRangeFilter, setActiveRangeFilter] = useState<ActiveRangeFilter>(null);
  const [inventorySummary, setInventorySummary] = useState<InventorySummaryData | null>(null);
  const [loadingSummary, setLoadingSummary] = useState<boolean>(false);
  const [paginationCursors, setPaginationCursors] = useState<Map<number, QueryDocumentSnapshot>>(new Map());
  const [lastVisibleDocument, setLastVisibleDocument] = useState<QueryDocumentSnapshot | undefined>(undefined);

  const fetchItems = useCallback(async (
    page: number = 1,
    pageSize: number = pagination.pageSize,
    cursorForPage?: QueryDocumentSnapshot,
    // Optional overrides for filters and activeRangeFilter
    filtersOverride?: InventoryFilterValues,
    activeRangeFilterOverride?: ActiveRangeFilter
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const isAuthenticated = auth?.currentUser !== null;
      if (!isAuthenticated) {
        setItems([]);
        setDataLoaded(true);
        setPagination(prev => ({ ...prev, totalItems: 0, totalPages: 1, currentPage: 1 }));
        throw new Error('Kullanıcı giriş yapmamış.');
      }

      const currentFiltersToUse = filtersOverride || filters;
      const currentActiveRangeFilterToUse = activeRangeFilterOverride !== undefined
                                          ? activeRangeFilterOverride
                                          : activeRangeFilter;

      const { items: fetchedItems, hasNextPage, lastVisibleDocument: newLastVisibleDoc } = await getAllItems(
        pageSize,
        page,
        cursorForPage,
        currentFiltersToUse, // Use the determined/overridden filters
        currentActiveRangeFilterToUse // Use the determined/overridden active range filter
      );

      setItems(fetchedItems);
      const newTotalItems = (page - 1) * pageSize + fetchedItems.length + (hasNextPage ? pageSize : 0);
      setPagination(prev => ({
        currentPage: page,
        pageSize: pageSize,
        totalItems: newTotalItems,
        totalPages: hasNextPage ? page + 1 : page,
      }));

      setLastVisibleDocument(newLastVisibleDoc);

      if (newLastVisibleDoc) {
        setPaginationCursors(prev => {
          const newCursors = new Map(prev);
          newCursors.set(page, newLastVisibleDoc);
          return newCursors;
        });
      }
      if (!hasNextPage) {
         setPaginationCursors(prev => {
            const newCursors = new Map(prev);
            for (const pageNum of Array.from(newCursors.keys())) {
                if (pageNum > page) {
                    newCursors.delete(pageNum);
                }
            }
            return newCursors;
        });
         setPagination(prev => ({ ...prev, totalPages: page }));
      }

      setDataLoaded(true);
    } catch (error: any) {
      console.error('Error fetching inventory items:', error);
      setError(error.message || 'Envanter öğeleri alınırken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  const setInventoryFilters = useCallback((newFilterValues: Partial<InventoryFilterValues>, newActiveRangeFilterValue?: ActiveRangeFilter) => {
    const updatedFilters = { ...filters, ...newFilterValues };
    let effectiveActiveRangeFilter = activeRangeFilter;

    if (newActiveRangeFilterValue !== undefined) {
        effectiveActiveRangeFilter = newActiveRangeFilterValue;
    }
    
    if (effectiveActiveRangeFilter === 'date') {
        updatedFilters.priceRange = null;
    } else if (effectiveActiveRangeFilter === 'price') {
        updatedFilters.dateRange = null;
    }

    setFilters(updatedFilters);
    // Always set the activeRangeFilter state based on the effective one for this operation
    setActiveRangeFilter(effectiveActiveRangeFilter);
    
    setPaginationCursors(new Map());
    setLastVisibleDocument(undefined);
    // Call fetchItems with the NEWLY calculated filter values as overrides
    fetchItems(1, pagination.pageSize, undefined, updatedFilters, effectiveActiveRangeFilter);
  }, [filters, activeRangeFilter, pagination.pageSize, fetchItems]);

  const fetchNextPage = async (): Promise<void> => {
    if (pagination.totalPages === 0 || pagination.currentPage < pagination.totalPages) {
       await fetchItems(pagination.currentPage + 1, pagination.pageSize, lastVisibleDocument);
    } else {
        console.log("Already on the last page or totalPages not determined for next.");
    }
  };

  const fetchPrevPage = async (): Promise<void> => {
    const prevPageNumber = pagination.currentPage - 1;
    if (prevPageNumber >= 1) {
      const cursorForStartOfPrevPage = prevPageNumber > 1 ? paginationCursors.get(prevPageNumber - 1) : undefined;
      await fetchItems(prevPageNumber, pagination.pageSize, cursorForStartOfPrevPage);
    }
  };

  const fetchItemById = async (id: string): Promise<InventoryItem | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await getItemById(id);
      return result;
    } catch (error) {
      setError('Ürün yüklenirken bir hata oluştu.');
      console.error('Error fetching item by ID:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createSilverItem = async (item: Omit<SilverItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<SilverItem> => {
    setLoading(true);
    setError(null);
    try {
      const result = await addSilverItem(item);
      // Refresh first page of inventory and the summary
      await fetchItems(1, pagination.pageSize);
      await fetchAndCalculateInventorySummary();
      return result;
    } catch (error) {
      setError('Ürün eklenirken bir hata oluştu.');
      console.error('Error creating item:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const createWatchItem = async (item: Omit<WatchItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<WatchItem> => {
    setLoading(true);
    setError(null);
    try {
      const result = await addWatchItem(item);
      // Refresh first page of inventory and the summary
      await fetchItems(1, pagination.pageSize);
      await fetchAndCalculateInventorySummary();
      return result;
    } catch (error) {
      setError('Ürün eklenirken bir hata oluştu.');
      console.error('Error creating item:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const modifyItem = async (id: string, item: Partial<Omit<BaseItem, 'id' | 'createdAt' | 'type'>>): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await updateItem(id, item);
      
      // Refresh current page of inventory and the summary
      await fetchItems(pagination.currentPage, pagination.pageSize);
      await fetchAndCalculateInventorySummary();

      if (selectedItem && selectedItem.id === id) {
        setSelectedItem(prev => prev ? { ...prev, ...item, updatedAt: new Date() } : null);
      }
    } catch (error) {
      setError('Ürün güncellenirken bir hata oluştu.');
      console.error('Error updating item:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await deleteItem(id);
      // Refresh current page of inventory and the summary
      await fetchItems(pagination.currentPage, pagination.pageSize);
      await fetchAndCalculateInventorySummary();
      
      if (selectedItem && selectedItem.id === id) {
        setSelectedItem(null);
      }
    } catch (error) {
      setError('Ürün silinirken bir hata oluştu.');
      console.error('Error deleting item:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const findByBarcode = async (barcode: string): Promise<InventoryItem | null> => {
    setLoading(true);
    setError(null);
    try {
      return await findItemByBarcode(barcode);
    } catch (error) {
      setError('Barkod ararken bir hata oluştu.');
      console.error('Error finding item by barcode:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const selectItem = (item: InventoryItem | null): void => {
    setSelectedItem(item);
  };

  const fetchAndCalculateInventorySummary = async (): Promise<void> => {
    setLoadingSummary(true);
    setError(null);
    try {
      const isAuthenticated = auth?.currentUser !== null;
      if (!isAuthenticated) {
        setInventorySummary(null);
        throw new Error('Kullanıcı giriş yapmamış.');
      }

      const summaryFilters: InventoryFilterValues = {
        status: ItemStatus.AVAILABLE,
        itemType: 'all',
        category: 'all',
        dateRange: null,
        priceRange: null,
        barcode: null,
      };
      const { items: summaryItems } = await getAllItems(9999, 1, undefined, summaryFilters, null);

      const totalItems = summaryItems.length;
      
      let calculatedTotalValueInTry = 0;
      if (summaryItems.length > 0) {
        const currentExchangeRate = await getCurrentExchangeRate();

        for (const item of summaryItems) {
          if (item.sellCurrency === Currency.TRY) {
            calculatedTotalValueInTry += item.sellPerUnit || 0;
          } else if (item.sellCurrency === Currency.USD) {
            if (currentExchangeRate > 0) {
              calculatedTotalValueInTry += convertUsdToTry(item.sellPerUnit || 0, currentExchangeRate);
            } else {
              console.warn(`Exchange rate not available for USD item ${item.id}. Its value won't be added to total.`);
            }
          }
        }
      }
      
      setInventorySummary({
        totalItems,
        totalValue: calculatedTotalValueInTry
      });

    } catch (err: any) {
      console.error('Error fetching inventory summary:', err);
      setError(err.message || 'Envanter özeti alınırken bir hata oluştu.');
      setInventorySummary(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  return (
    <InventoryContext.Provider value={{
      items,
      loading,
      error,
      selectedItem,
      pagination,
      filters,
      activeRangeFilter,
      inventorySummary,
      loadingSummary,
      fetchItems,
      setInventoryFilters,
      fetchNextPage,
      fetchPrevPage,
      fetchAndCalculateInventorySummary,
      fetchItemById,
      createSilverItem,
      createWatchItem,
      modifyItem,
      removeItem,
      findByBarcode,
      selectItem,
      dataLoaded
    }}>
      {children}
    </InventoryContext.Provider>
  );
}; 